/* Garde de session + réconciliation d'organisation — Factumis app (Task 7).

   Protège l'arbre de l'app (Layout + écrans). Trois cas de redirection :
   - aucune session            → /connexion
   - session sans org du tout  → /onboarding (premier lancement légitime)
   - session connectée + org    → rend l'app (<Outlet/>)

   RÉCONCILIATION (évite la boucle de doublons d'organisation) :
   une session peut exister sans `activeOrganizationId` alors que
   l'utilisateur APPARTIENT déjà à une organisation (ex. setActive a échoué,
   ou membre de retour). Dans ce cas on ne redirige PAS vers /onboarding
   (sinon création de doublon) : on appelle setActive avec la 1re org connue,
   puis on entre dans l'app une fois la session rafraîchie. /onboarding n'est
   donc atteint que par un utilisateur sans AUCUNE organisation.

   États d'erreur (jamais de spinner bloqué, jamais de retombée /onboarding) :
   - le fetch de session échoue (backend down/500)  → panneau "service
     indisponible" + Réessayer (refetch), distinct du cas "pas de session".
   - setActive échoue (membre retiré, 500, réseau)  → panneau d'erreur +
     Réessayer (relance l'effet) + lien Se reconnecter, jamais /onboarding.

   API Better Auth vérifiée (node_modules, 1.6.x) :
   - useSession() → { data, isPending, isRefetching, error, refetch } ;
     data = { session, user } ; activeOrganizationId vit sur
     data.session.activeOrganizationId (champ ajouté par le plugin org).
   - authClient.useListOrganizations() → { data, isPending, error } (atom
     `listOrganizations` exposé en hook `use…`).
   - authClient.organization.setActive({ organizationId }) → { data, error }. */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { authClient, useSession } from '../lib/auth-client';
import { AuthLayout } from './AuthLayout';
import { Button } from '../components/ui';
import { Icon } from '../lib/icons';

/* Loader léger centré, sans texte. Réutilise l'animation `sp` déjà définie
   globalement (cf. .btn .spin dans app.css). */
function Loader() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span
        aria-label="Chargement"
        role="status"
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: '3px solid currentColor',
          borderTopColor: 'transparent',
          opacity: 0.5,
          animation: 'sp .7s linear infinite',
        }}
      />
    </div>
  );
}

export function RequireSession() {
  const { data: session, isPending, error: sessionError, refetch } = useSession();
  const location = useLocation();
  const hasActiveOrg = Boolean(session?.session.activeOrganizationId);

  // On ne liste les organisations que lorsque c'est utile (session connectée
  // sans org active). Le hook est toujours appelé (règle des hooks) ; ses
  // données ne sont exploitées que dans la branche de réconciliation.
  const { data: orgs, isPending: orgsPending } = authClient.useListOrganizations();

  // Évite d'appeler setActive en boucle : on retient l'id pour lequel la
  // tentative a déjà été lancée. `reconcileFailed` casse le spinner si la
  // tentative a échoué (sinon : loader infini, erreur avalée).
  const reconciledFor = useRef<string | null>(null);
  const [reconcileFailed, setReconcileFailed] = useState(false);

  const needsReconcile = Boolean(session) && !hasActiveOrg;
  const firstOrgId = orgs && orgs.length > 0 ? orgs[0].id : null;

  useEffect(() => {
    if (!needsReconcile || !firstOrgId) return;
    if (reconciledFor.current === firstOrgId) return;
    // Load-bearing : on marque l'id AVANT l'appel async pour que le double
    // montage de StrictMode ne lance pas deux setActive — ne pas déplacer
    // cette ligne après le .then().
    reconciledFor.current = firstOrgId;
    // setActive rafraîchit la session (signal $activeOrgSignal) : useSession
    // se met à jour, hasActiveOrg passe à true et l'<Outlet/> est rendu.
    authClient.organization.setActive({ organizationId: firstOrgId }).then(({ error }) => {
      if (error) setReconcileFailed(true);
    });
  }, [needsReconcile, firstOrgId]);

  // 1) Session en cours de résolution → loader.
  if (isPending) return <Loader />;

  // 1bis) Le fetch de session a échoué (backend down/500) — distinct de
  //       "pas de session". On ne redirige PAS vers /connexion : on propose
  //       de réessayer.
  if (sessionError) {
    return (
      <AuthLayout title="Service indisponible" subtitle="La connexion au service a échoué.">
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          Connexion au service impossible, réessayez.
        </div>
        <div className="auth-actions">
          <Button type="button" full onClick={() => void refetch()}>
            Réessayer
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // 2) Pas de session → connexion, en mémorisant la destination demandée
  //    (?next=) pour y revenir après login plutôt que de retomber sur /dashboard.
  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/connexion?next=${next}`} replace />;
  }

  // 3) Session + org active → app.
  if (hasActiveOrg) return <Outlet />;

  // 4) Pas d'org active : on attend la liste des organisations.
  if (orgsPending) return <Loader />;

  // 4a) L'utilisateur a ≥1 org → réconciliation via setActive (effet).
  if (firstOrgId) {
    // La réconciliation a échoué (org retirée, 500, réseau) : on évite le
    // spinner infini. SURTOUT pas de retombée /onboarding (risque de doublon).
    if (reconcileFailed) {
      return (
        <AuthLayout
          title="Espace inaccessible"
          subtitle="Impossible d'activer votre organisation."
        >
          <div className="auth-error" role="alert">
            <Icon name="alert" size={16} />
            Impossible d'accéder à votre espace, réessayez.
          </div>
          <div className="auth-actions">
            <Button
              type="button"
              full
              onClick={() => {
                // Réinitialise le verrou pour relancer l'effet setActive sur
                // le même org id (l'effet re-teste reconciledFor !== firstOrgId).
                reconciledFor.current = null;
                setReconcileFailed(false);
              }}
            >
              Réessayer
            </Button>
          </div>
          <div className="auth-foot-row">
            <Link to="/connexion" className="auth-link">
              Se reconnecter
            </Link>
          </div>
        </AuthLayout>
      );
    }
    // En cours de réconciliation → loader le temps que la session se rafraîchisse.
    return <Loader />;
  }

  // 4b) Aucune org → onboarding (premier lancement légitime).
  return <Navigate to="/onboarding" replace />;
}
